import { NativeSelect } from "@admin-ui/components/base/select/select-native";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import type { ChangeEvent, FormEvent } from "react";

const PDF_ACCEPT = "application/pdf";

export interface NewViewPdfFile {
  id: string;
  file: { name: string };
  status: "uploading" | "completed" | "failed";
  progress: number;
}

export interface NewViewProps {
  accountOptions: { value: string; label: string }[];
  accountId: string;
  name: string;
  websiteUrl: string;
  nameError?: string | string[];
  pdfFiles: NewViewPdfFile[];
  isSubmitDisabled: boolean;
  cancelHref: string;
  onSubmit: (e: FormEvent) => void;
  onAccountIdChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onWebsiteUrlChange: (value: string) => void;
  onPdfFilesChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemovePdf: (id: string) => void;
}

export function NewView({
  accountOptions,
  accountId,
  name,
  websiteUrl,
  nameError,
  pdfFiles,
  isSubmitDisabled,
  cancelHref,
  onSubmit,
  onAccountIdChange,
  onNameChange,
  onWebsiteUrlChange,
  onPdfFilesChange,
  onRemovePdf,
}: NewViewProps) {
  return (
    <div className="mx-auto w-full max-w-xl px-4 lg:px-8">
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <NativeSelect
          label="Community account"
          options={accountOptions}
          value={accountId}
          onChange={(e) => onAccountIdChange(e.target.value)}
          required
        />

        <Input
          label="Program name"
          placeholder="e.g. Field School of Hvar"
          value={name}
          onChange={onNameChange}
          isRequired
          hint={nameError}
          isInvalid={Boolean(nameError)}
        />

        <fieldset className="flex flex-col gap-3 rounded-lg border border-secondary p-4">
          <legend className="px-1 font-medium text-secondary text-sm">
            AI sources <span className="font-normal text-tertiary">(optional)</span>
          </legend>
          <p className="text-secondary text-sm">
            Add a website URL and any PDFs to have Claude pre-fill tagline, description, age range, and more. Skip this
            and you'll fill the form yourself.
          </p>

          <Input
            label="Website URL"
            placeholder="https://example.com"
            value={websiteUrl}
            onChange={onWebsiteUrlChange}
          />

          <div className="flex flex-col gap-2">
            <label className="font-medium text-secondary text-sm" htmlFor="pdf-upload">
              PDF brochures (≤10 MB each)
            </label>
            <input
              id="pdf-upload"
              type="file"
              accept={PDF_ACCEPT}
              multiple
              onChange={onPdfFilesChange}
              className="text-sm"
            />
            {pdfFiles.length > 0 && (
              <ul className="flex flex-col gap-1 text-sm">
                {pdfFiles.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-2 text-tertiary">
                    <span className="truncate">{f.file.name}</span>
                    <span>
                      {f.status === "uploading" && `${f.progress}%`}
                      {f.status === "completed" && "ready"}
                      {f.status === "failed" && "failed"}
                    </span>
                    <button type="button" onClick={() => onRemovePdf(f.id)} className="text-utility-red-600">
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </fieldset>

        <div className="flex justify-end gap-2 pt-4">
          <Button color="secondary" href={cancelHref}>
            Cancel
          </Button>
          <Button color="primary" type="submit" isDisabled={isSubmitDisabled}>
            Create program
          </Button>
        </div>
      </form>
    </div>
  );
}
